import React, { useMemo } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { useHelpTypography } from '../../hooks/useHelpTypography';
import { helpI18n } from '../../i18n/help';

type MetaRow = { label: string; description: string };
type SectionRow = { label: string; description?: string };

type ParsedReport = {
  meta: MetaRow[];
  sections: SectionRow[];
  notes: { kind: 'tip' | 'note'; text: string }[];
};

const META_SPLIT = /\s+[—–-]\s+/;
const BULLET_RE = /^[•\-\*]\s*/;

function splitLabel(line: string): { label: string; description?: string } {
  const parts = line.split(META_SPLIT);
  if (parts.length >= 2) {
    return { label: parts[0].trim(), description: parts.slice(1).join(' — ').trim() };
  }
  return { label: line.trim() };
}

function parseReportContent(content: string): ParsedReport {
  const blocks = content
    .split(/\n\n+/)
    .map((block) => block.trim())
    .filter(Boolean);

  const meta: MetaRow[] = [];
  const sections: SectionRow[] = [];
  const notes: ParsedReport['notes'] = [];

  for (const block of blocks) {
    const lines = block
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) continue;

    const first = lines[0];
    const tipMatch = first.match(/^(tip|نصيحة|مشورہ|सुझाव|টিপ|ඉඟිය|सुझाव)\s*:\s*(.*)$/i);
    const noteMatch = first.match(/^(note|ملاحظة|نوٹ|नोट|নোট|සටහන)\s*:\s*(.*)$/i);

    if (tipMatch || noteMatch) {
      const match = tipMatch ?? noteMatch!;
      const rest = [match[2], ...lines.slice(1)].filter(Boolean).join(' ').trim();
      notes.push({ kind: tipMatch ? 'tip' : 'note', text: rest });
      continue;
    }

    const bulletLines = lines.filter((line) => BULLET_RE.test(line));
    const isSectionsBlock =
      bulletLines.length > 0 &&
      (lines[0].endsWith(':') || /inside|يتضمن|اندر|अंदर|ভেতরে|ඇතුළත|भित्र/i.test(lines[0]));

    if (isSectionsBlock) {
      for (const line of bulletLines) {
        const cleaned = line.replace(BULLET_RE, '').trim();
        const { label, description } = splitLabel(cleaned);
        sections.push({ label, description });
      }
      continue;
    }

    for (const line of lines) {
      const { label, description } = splitLabel(line);
      if (description) {
        meta.push({ label, description });
      }
    }
  }

  return { meta, sections, notes };
}

function metaIcon(label: string): SvgIconComponent {
  const value = label.toLowerCase();
  if (/definition|تعريف|تعریف|परिभाषा|সংজ্ঞা|නිර්වචන/.test(value)) {
    return MenuBookOutlinedIcon;
  }
  if (/content|محتوى|مواد|सामग्री|বিষয়বস্তু|අන්තර්ගත/.test(value)) {
    return DescriptionOutlinedIcon;
  }
  return CalendarMonthOutlinedIcon;
}

interface ReportArticleContentProps {
  content: string;
}

const ReportArticleContent: React.FC<ReportArticleContentProps> = ({ content }) => {
  const theme = useTheme();
  const { fontFamily } = useHelpTypography();
  const { i18n } = useTranslation('translation', { i18n: helpI18n });
  const isArabic = i18n.language.startsWith('ar');
  const sectionsHeading = isArabic ? 'أقسام التقرير' : 'Report sections';
  const parsed = useMemo(() => parseReportContent(content), [content]);

  const cardBorder = theme.palette.divider;
  const softBg = theme.palette.background.elevated;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: 880,
        width: '100%',
        fontFamily,
        '& .MuiTypography-root': { fontFamily: 'inherit' },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          borderRadius: '16px',
          border: `1px solid ${cardBorder}`,
          bgcolor: theme.palette.background.paper,
          boxShadow:
            theme.palette.mode === 'dark'
              ? theme.shadows[2]
              : '0 4px 16px rgba(15, 23, 42, 0.06)',
        }}
      >
        {parsed.meta.length > 0 ? (
          <Box sx={{ px: 2.5, py: 2, bgcolor: softBg, borderBottom: parsed.sections.length ? `1px solid ${theme.palette.divider}` : 'none' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              {parsed.meta.map((row) => {
                const Icon = metaIcon(row.label);
                return (
                  <Box
                    key={`${row.label}-${row.description}`}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: 1.5,
                      minHeight: { md: 120 },
                      borderRadius: '12px',
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#FFFFFF',
                      border: `1px solid ${cardBorder}`,
                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '9px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${theme.palette.primary.main}12`,
                        color: 'primary.main',
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          color: 'primary.main',
                          mb: 0.5,
                          fontSize: '0.6875rem',
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ lineHeight: 1.55, color: 'text.secondary', fontSize: '0.8125rem' }}
                      >
                        {row.description}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null}

        {parsed.sections.length > 0 ? (
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: 'text.secondary',
                mb: 1.25,
                lineHeight: 1.2,
              }}
            >
              {sectionsHeading}
            </Typography>
            <Box
              sx={{
                borderRadius: '12px',
                border: `1px solid ${cardBorder}`,
                overflow: 'hidden',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#FFFFFF',
              }}
            >
              {parsed.sections.map((row, index) => (
                <Box
                  key={`${row.label}-${row.description ?? index}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'auto 1fr', sm: '36px 160px 1fr' },
                    alignItems: 'start',
                    gap: { xs: 1.25, sm: 2 },
                    px: 1.75,
                    py: 1.35,
                    borderTop: index > 0 ? `1px solid ${theme.palette.divider}` : 'none',
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${theme.palette.primary.main}12`,
                      color: 'primary.main',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.45,
                      color: 'text.primary',
                      fontWeight: 600,
                      gridColumn: { xs: 2, sm: 'auto' },
                    }}
                  >
                    {row.label}
                  </Typography>
                  {row.description ? (
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: 1.55,
                        color: 'text.secondary',
                        fontSize: '0.8125rem',
                        gridColumn: { xs: '1 / -1', sm: 'auto' },
                        pl: { xs: '40px', sm: 0 },
                      }}
                    >
                      {row.description}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Paper>

      {parsed.notes.map((note, index) => {
        const isTip = note.kind === 'tip';
        const Icon = isTip ? LightbulbOutlinedIcon : InfoOutlinedIcon;
        return (
          <Paper
            key={`${note.kind}-${index}`}
            elevation={0}
            sx={{
              display: 'flex',
              gap: 1.5,
              p: 2,
              borderRadius: '14px',
              border: `1px solid ${cardBorder}`,
              bgcolor: isTip
                ? theme.palette.mode === 'dark'
                  ? 'rgba(250, 204, 21, 0.08)'
                  : '#FFFBEB'
                : softBg,
            }}
          >
            <Icon sx={{ color: isTip ? 'warning.main' : 'info.main', mt: 0.25 }} />
            <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'text.secondary' }}>
              {note.text}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
};

export default ReportArticleContent;
